import {
  findShippingRegion,
  type ShippingAddress,
  type ShippingRegion,
  type UpdateOrderAddressInput,
  updateOrderAddressInputSchema,
} from '@repo/api-contracts';
import { type FormEvent, type ReactElement, useRef, useState } from 'react';
import {
  isOrderConflict,
  orderMutationMessage,
  SHIPPING_REGION_CHANGED_MESSAGE,
  useModalDialog,
} from '@/shared/lib';
import { Button, DialogFrame, ShippingAddressFields } from '@/shared/ui';

interface OrderAddressDialogProps {
  readonly orderId: string;
  /** 바꿀 주문 배송지(배송지 카드의 [배송지 변경]). */
  readonly destinationId: string;
  /** 배송지 카드 번호(1부터) — 창 제목 `배송지 N 변경`. */
  readonly destinationNumber: number;
  /** 그 배송지 우편번호의 지역 — 고객은 같은 지역 안에서만 바꾼다(spec D11). 알 수 없으면 null(지역 제한 없음, 교차 조정 R1). */
  readonly region: ShippingRegion | null;
  readonly address: ShippingAddress;
  readonly onSubmit: (input: UpdateOrderAddressInput) => Promise<void>;
  readonly onReload: () => void;
  readonly onClose: () => void;
  readonly pending?: boolean;
  readonly error?: string | null;
}

/**
 * 배송지 변경(주문 설계 §8-3, SUPER-86 spec §5-4) — 배송지 하나의 지금 주소를 기본값으로 견적 요청 폼과 같은 배송지 입력을 쓴다.
 * 지역 구분이 바뀌는 주소(배송비가 달라진다)는 보내지 않고 견적 수정을 안내한다(서버와 같은 판단, D11). 실패하면 입력을 지우지 않고
 * 안내하고, 상태 충돌이면 상세를 다시 읽는다. 처리 중에는 Escape 로 닫지 않는다. 모양은 공용 대화상자 틀(DialogFrame)이다.
 */
export const OrderAddressDialog = ({
  orderId,
  destinationId,
  destinationNumber,
  region,
  address,
  onSubmit,
  onReload,
  onClose,
  pending = false,
  error,
}: OrderAddressDialogProps): ReactElement => {
  const [value, setValue] = useState(address);
  const [message, setMessage] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const lock = useRef(false);
  const modal = useModalDialog(onClose, !(pending || saving));
  const shown = message || error;

  const submit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    if (pending || lock.current) return;
    const parsed = updateOrderAddressInputSchema.safeParse({
      orderId,
      destinationId,
      shippingAddress: value,
    });
    if (!parsed.success) {
      setMessage('배송지 정보를 모두 입력해 주세요.');
      return;
    }
    if (region !== null && findShippingRegion(parsed.data.shippingAddress.postalCode) !== region) {
      setMessage(SHIPPING_REGION_CHANGED_MESSAGE);
      return;
    }
    lock.current = true;
    setSaving(true);
    setMessage(null);
    try {
      // 성공 뒤 상세는 쓰기 훅이 응답(최신 상세)으로 갱신한다 — 같은 상세를 다시 읽지 않는다.
      await onSubmit(parsed.data);
      onClose();
    } catch (failure) {
      setMessage(orderMutationMessage(failure));
      if (isOrderConflict(failure)) onReload();
    } finally {
      lock.current = false;
      setSaving(false);
    }
  };

  return (
    <DialogFrame
      modal={modal}
      title={`배송지 ${destinationNumber} 변경`}
      className="max-w-lg"
      onSubmit={(event) => {
        void submit(event);
      }}
      footer={
        <>
          <Button type="submit" isLoading={saving} disabled={pending}>
            배송지 저장
          </Button>
          <Button type="button" variant="ghost" disabled={pending || saving} onClick={onClose}>
            닫기
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <ShippingAddressFields
          value={value}
          onChange={(patch) => setValue((previous) => ({ ...previous, ...patch }))}
          disabled={saving || pending}
        />
        {shown ? (
          <p role="alert" className="text-destructive">
            {shown}
          </p>
        ) : null}
      </div>
    </DialogFrame>
  );
};
